import { action } from 'mobx'

import BaseListStore, { IBaseListAddOptions } from 'stores/base/BaseListStore'

import getRecordId from 'utils/getRecordId'
import createError from 'utils/createError'

import Record, { IRecordCreatePayload } from './Record'

class RecordStore extends BaseListStore<Record, IRecordCreatePayload> {
  constructor() {
    super(Record)
  }

  @action
  add(
    record: Omit<IRecordCreatePayload, 'id'> & { id?: string },
    options: IBaseListAddOptions = {}
  ): Record {
    const createdRecordId =
      record.id ?? getRecordId(record.modelId, record.value)

    if (!createdRecordId) {
      throw createError(
        'RecordStore.add',
        'Unable to determine ID for record to create',
        {
          record,
        }
      )
    }

    const createdRecord = super.add({
      id: createdRecordId,
      ...record,
    })

    // Create related records as well
    Object.keys(record.value).forEach(fieldName => {
      const field = createdRecord.model.getFieldByName(fieldName)
      if (!field) {
        throw createError('RecordStore.add', 'Invalid field name', {
          fieldName,
          recordValue: record.value,
        })
      }

      if (!field.isRelation) {
        // If the field is not a relation, no additional records need to be created
        return
      }

      const fieldTypeAsModelId = field.typeAsModel?.id
      if (!fieldTypeAsModelId) {
        // If the model this field is related to does not exist
        throw createError(
          'RecordStore.add',
          'Unable to create related records',
          { fieldId: field.id, type: field.type }
        )
      }

      if (field.isList) {
        record.value[fieldName].map((v: any) => {
          const relatedRecordId = getRecordId(fieldTypeAsModelId, v)
          const relatedRecord = this.get(relatedRecordId)
          // Don't create the record if it already exists. Normally, BaseListStore would handle this, and it would try to update if it already exists
          // Since this add might be called multiple times really quickly, we can get rid of that extra computation everytime pre-emptively.
          !relatedRecord &&
            v &&
            this.add(
              {
                modelId: fieldTypeAsModelId,
                value: v,
              },
              options
            )
        })
      } else if (record.value[fieldName] !== null) {
        // If the field is a normal relation (that isn't null)
        const relatedRecordId = getRecordId(
          fieldTypeAsModelId,
          record.value[fieldName]
        )
        const relatedRecord = this.get(relatedRecordId)

        !relatedRecord &&
          record.value[fieldName] &&
          this.add(
            {
              modelId: fieldTypeAsModelId,
              value: record.value[fieldName],
            },
            options
          )
      }
    })

    return createdRecord
  }
}

export default new RecordStore()
